import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Cake, Search, Download, Mail, MessageCircle, CalendarCheck, Loader2 } from "lucide-react";

const PERIODS = [
  { key: "today", label: "Oggi" },
  { key: "tomorrow", label: "Domani" },
  { key: "next7", label: "Prossimi 7gg" },
  { key: "next30", label: "Prossimi 30gg" },
];

const fmtDate = (s) => s ? String(s).split("T")[0].split("-").reverse().join("/") : "—";

export function BirthdayReport({ onNavigate }) {
  const [period, setPeriod] = useState("next30");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/birthdays?period=${period}&q=${encodeURIComponent(q)}`)
      .then((r) => setData(r.data)).catch(() => setData({ items: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [period, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const sendGreeting = async (client, channel) => {
    setSending((s) => ({ ...s, [`${client.id}:${channel}`]: true }));
    try {
      const { data: res } = await api.post(`/clients/${client.id}/birthday-greeting`, { channel });
      if (res.ok) toast.success(`Auguri inviati a ${client.name} via ${channel === "email" ? "Email" : "WhatsApp"} (sandbox)`);
      else toast.info(res.reason || "Nessun invio effettuato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setSending((s) => ({ ...s, [`${client.id}:${channel}`]: false })); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/reports/birthdays/export.csv?period=${period}&q=${encodeURIComponent(q)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `compleanni_${period}.csv`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore esportazione"); }
    finally { setExporting(false); }
  };

  const items = data?.items || [];

  return (
    <Card className="p-5" data-testid="dashboard-birthdays">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
          <Cake className="h-5 w-5 text-[hsl(var(--hi-gold))]" /> Report Compleanni
          {data?.with_appointment > 0 && <Badge variant="secondary" className="ml-1">{data.with_appointment} con appuntamento</Badge>}
        </h3>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || items.length === 0} data-testid="birthday-export-csv">
          {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Esporta CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="birthday-period-tabs">
            {PERIODS.map((p) => <TabsTrigger key={p.key} value={p.key} data-testid={`birthday-period-${p.key}`}>{p.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente..." className="pl-9 h-9" data-testid="birthday-search" />
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="birthday-empty">Nessun compleanno nel periodo selezionato.</div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto hi-scroll pr-1">
          {items.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 hover:bg-accent/50 transition-colors" data-testid={`birthday-row-${b.id}`}>
              <button onClick={() => onNavigate && onNavigate(b.id)} className="text-left min-w-0 flex-1">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {b.name || "Cliente"}
                  {b.days === 0 && <Badge className="text-[10px] bg-[hsl(var(--hi-gold))] text-black hover:bg-[hsl(var(--hi-gold))]">Oggi</Badge>}
                  {b.has_appointment && <Badge variant="outline" className="text-[10px] gap-1"><CalendarCheck className="h-3 w-3" />Appuntamento</Badge>}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {fmtDate(b.birthdate)} · {b.days === 0 ? "oggi" : b.days === 1 ? "domani" : `tra ${b.days} giorni`}
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!b.can_email || sending[`${b.id}:email`]}
                  onClick={() => sendGreeting(b, "email")} title={b.can_email ? "Invia auguri via Email" : "Email non disponibile o consenso mancante"}
                  data-testid={`birthday-email-${b.id}`}>
                  {sending[`${b.id}:email`] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!b.can_whatsapp || sending[`${b.id}:whatsapp`]}
                  onClick={() => sendGreeting(b, "whatsapp")} title={b.can_whatsapp ? "Invia auguri via WhatsApp" : "WhatsApp non disponibile o consenso mancante"}
                  data-testid={`birthday-whatsapp-${b.id}`}>
                  {sending[`${b.id}:whatsapp`] ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-3">Gli invii avvengono in modalità sandbox finché le integrazioni Email/WhatsApp non sono attivate.</p>
    </Card>
  );
}
